import configJSON from 'config.json';
import { AreaPageListing } from '@zoocasa/go-search';
import { AreaPageListingCardData } from 'components/pages/area-listings/area_page_listing_card_data';
import { createGetServerSideProps } from 'utils/next/shared-getServerSideProps';
import type { FeaturesType } from 'contexts/features';
import type { TenantTypes } from '@tenants/manager';
import { getComplianceRequirementFromRequest } from 'utils/compliance';
import type { ExpListingCardDataWithCompliance } from 'types/listing-card';
import { toExpListingCardDataWithCompliance } from 'utils/listing-card/listing-card-transformers';
import { AreaListingsRouteMatchObject, generateRouteMatchObjectFromPath, ROOT_ROUTE } from 'components/dynamic-page/route-matchers';
import HomePage, { HomePageProps, HomePageInsights } from 'components/home-page';
import AgntHomeRoot, { getAgntHomeServerSideProps, type AgntHomeRootProps } from 'agnt/pages/home/root';
import { fetchHomePageListings, fetchHomePageListingsWithBoundary, ListingsQueryType } from 'components/home-page/fetcher';
import { ListingCardData } from 'components/listing-card';
import { getLastSearchLocationTag, HIDE_NOTIFICATION_BANNER_COOKIE_NAME, ENABLE_LOCAL_FOCUS_HOMEPAGE_COOKIE_NAME } from 'constants/cookies';
import { CACHE_CONTROL_HEADER_NAME } from 'constants/headers';
import { getFeaturesOverrideFromRequest } from 'utils/features';
import { defaultHomeType } from 'constants/listing-components';
import { defaultCACityPayload, defaultUSCityPayload } from 'constants/locations';
import { getSiteLocationFromRequest } from 'utils/site-location';
import { AREA_PAGE_CONSTANTS } from 'constants/pagination';
import { LastSearchLocation } from 'contexts';
import { AVAILABLE_STATUS, Filter, ListingParams, NOT_AVAILABLE_SOLD_STATUS } from 'contexts/preferences/listing-params/types';
import { createNamedContentsApi } from 'data/named-content';
import { checkLocationHasAreaPageWithId, MILITARY_BASES_SEARCH_PREDICTION_GROUP, getLocationSlugFromId, normalizeDescription } from 'data/search-predictions';
import { getListingByMls } from 'data/listing';
import { AreaPageListing_Status } from '@zoocasa/go-search';
import { useMemo } from 'react';
import { PartialDeep } from 'type-fest';
import { CountryCodeList, type CountryCode } from 'types/countries';
import { boundaryAroundPoint, getNSEWWithBoundaryString } from 'utils/boundary';
import { getObjectFromRequestCookie, getStringFromRequestCookie } from 'utils/cookies';
import isUserAgentCrawler from 'utils/crawler-agent';
import extractPageFromUrl, { DEFAULT_PAGE_NUMBER } from 'utils/extract-page-from-url';
import { getSlugFallbackFromURLSearchParams, getSortFromURLSearchParams } from 'utils/listing-query-helper';
import { countryCodeFromProvinceOrState, provinceOrStateNameFromCode, ProvinceOrStateCode, citySlugFromCityProvince } from 'utils/province_or_state';
import { sanitizeProvinceOrStateCode } from 'utils/sanitize';
import { getUserLocationFromServerSideRequest } from 'utils/user-location';
import { isLastSearchLocationValid } from 'utils/validate-location';
import { ThemeNames } from 'types/themes';
import {
  EXP_IN_SERVICE_TENANT_ID,
  EXP_REALTY_CA_TENANT_ID,
  EXP_REALTY_US_TENANT_ID,
  isTenantAgntInc,
  isTenantExpCA,
  isTenantExpCommercial,
  isTenantExpCommercialCa,
  isTenantExpInService,
  isTenantExpUS,
  isZoocasaTenant,
  ZOOCASA_TENANT_ID,
} from 'tenants';
import { getBlogArticlesByOffset } from 'data/blog-article';
import { CommercialBlogPostsApi } from 'data/wordpress/utils';
import { HeadDataType } from 'components/head-data';
import { extractLocationForTenant, getSupportedUserLocation } from 'utils/user-location/user-location';
import { ZOO_CA_HEAD_DATA, ZOO_USA_HEAD_DATA } from 'constants/seo';
import { createInsightsApi } from 'data/insights';
import { getCityInsights } from 'data/city-insights/fetcher';
import {
  selectLocalizedCityInsights,
  normalizeLocaleToInsightsLang,
  isLocaleTranslationMissing,
} from 'utils/city-insights/localize';
import { parseCityAndProvince, topCitiesToAreaCards, isCitySlug } from 'components/home-page/themed-home-page-index/homepage-local-focus/derivation';
import { getTenantHomeConfig } from '@tenants/utils/configs';
import { standardizedCityName } from 'utils/standardized-city-name';
import { buildMapSearchUrlForUserLocation, generateHomepageUrls } from 'components/home-page/homepage-helper';

import type { InsightsApiType } from 'data/insights/api';
import type { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import type { LinkDataType } from 'components/home-page/internal-links';
import { getLastSearchDescription } from 'utils/search-predictions';
import { Namespaces } from 'types/locales';
import type { GenerateUrlsResult } from 'components/home-page/homepage-helper';
import { COMMERCIAL_LIFE_CATEGORY_ID, INSIGHTS_BLOG_PATH } from './insights';
import { formatI18nDate } from '../utils/i18n-format-date';

const POPULAR_US_STATES_COUNT = 11;
const POPULAR_US_CITIES_COUNT = 12;

/**
 * Formats a city label with its province/state code
 * @param cityName - The name of the city
 * @param citySlug - The city slug (e.g., "houston-tx" or "toronto-on")
 * @param isRental - Whether this is a rental listing
 * @returns Formatted label (e.g., "Houston, TX Homes For Sale" or "Toronto Homes for Rent")
 */
const formatCityLabel = (cityName: string, citySlug: string, isRental: boolean = false): string => {
  const provinceCode = citySlug.split('-').pop()?.toUpperCase();
  const listingType = isRental ? 'Homes for Rent' : 'Homes For Sale';
  return `${standardizedCityName(cityName)}, ${provinceCode} ${listingType}`;
};

/**
 * Generates internal links for a given country
 * @param countryCode - The country code to generate links for
 * @param insightsApi - The insights API instance
 * @param internalLinksJSON - The internal links JSON data
 * @returns Promise of LinkDataType array
 */
const generateInternalLinksForCountry = async (
  countryCode: CountryCode,
  insightsApi: InsightsApiType,
  internalLinksJSON: any
): Promise<LinkDataType[]> => {
  // Get all data in parallel using Promise.all
  const [topCities, topStates, topRentalCities] = await Promise.all([
    insightsApi.getTopCities(countryCode, undefined, undefined, POPULAR_US_CITIES_COUNT),
    insightsApi.getAllProvincesOrStates(countryCode).then(states => states?.slice(0, POPULAR_US_STATES_COUNT)),
    insightsApi.getTopCities(countryCode, undefined, undefined, POPULAR_US_CITIES_COUNT, true),
  ]);

  const internalLinks: LinkDataType[] = [];

  // Add Popular States/Provinces section
  if (topStates) {
    internalLinks.push({
      title: countryCode === CountryCodeList.UNITED_STATES ? 'Popular US States' :
        countryCode === CountryCodeList.CANADA ? 'Popular Canadian Provinces' :
          'Popular Regions',
      links: [
        {
          label: 'All US real estate listings',
          link: '/us-real-estate',
        },
        ...topStates.map(state => {
          const stateName = provinceOrStateNameFromCode(state.Province.toLowerCase() as ProvinceOrStateCode);
          const formattedStateSlug = stateName.toLowerCase().replace(/ /g, '-');
          return {
            label: `${stateName} Homes For Sale`,
            link: `/${formattedStateSlug}-real-estate`,
          };
        }),
      ],
    });
  }

  // Add Popular Cities section
  if (topCities) {
    internalLinks.push({
      title: countryCode === CountryCodeList.UNITED_STATES ? 'Popular US Cities' :
        countryCode === CountryCodeList.CANADA ? 'Popular Canadian Cities' :
          'Popular Cities',
      links: topCities.map(city => ({
        label: formatCityLabel(city.Name, city.Slug),
        link: `/${city.Slug}-real-estate`,
      })),
    });
  }

  // Add Popular Searches section
  if (countryCode === CountryCodeList.UNITED_STATES) {
    const popularSearches = internalLinksJSON.find(
      (section: any) => section.title === 'Popular USA Searches'
    );
    if (popularSearches) {
      internalLinks.push({
        title: 'Popular Searches',
        links: popularSearches.links,
      });
    }
  }

  // Add Popular Rental Cities section
  if (topRentalCities) {
    internalLinks.push({
      title: 'Popular Rental Cities',
      links: topRentalCities.map(city => ({
        label: formatCityLabel(city.Name, city.Slug, true),
        link: `/${city.Slug}-real-estate/for-rent`,
      })),
    });
  }

  return internalLinks;
};

export interface INamedContents {
  featuredHomesMlsNums: string[];
}

/**
 * Resolves and fetches the insights data (city stats, popular areas, and the
 * AI Deep Dive) for the local-focus homepage server-side, based on a location
 * resolved from the request (Cloudflare geo / IP) rather than a client cookie.
 *
 * The data seeds the At a Glance, Area Cards, Market Dynamics, and City Insights
 * sections so they render with real data on first paint. Returns `null` when no
 * usable location is available; all fetch failures degrade gracefully to null.
 */
function resolveLastSearchInsightsLocation(
  lastSearchLocation?: LastSearchLocation
): { slug: string; name: string; latitude: number; longitude: number } | null {
  if (!lastSearchLocation?.description) return null;

  const normalizedName = normalizeDescription(lastSearchLocation.description);
  const rawSlug = lastSearchLocation.slug || lastSearchLocation.id?.replace(/^location-/, '') || '';
  const { city, province } = parseCityAndProvince(normalizedName, rawSlug);
  if (!city || !province) return null;

  const citySlug = citySlugFromCityProvince(city, province);
  if (!isCitySlug(citySlug)) return null;

  return {
    slug: citySlug,
    name: normalizedName,
    latitude: 0,
    longitude: 0,
  };
}

async function resolveHomepageInsights(
  country: CountryCode,
  userLocation?: { slug?: string; name?: string; latitude?: number; longitude?: number },
  locale?: string
): Promise<HomePageInsights | null> {
  const slug = userLocation?.slug;
  if (!slug) return null;

  const name = userLocation?.name || slug;
  const { city, province } = parseCityAndProvince(name, slug);
  const provinceCode = province as ProvinceOrStateCode;
  const insightsApi = createInsightsApi();

  const [cityStats, topCities, storedCityInsights] = await Promise.all([
    (async () => {
      let stats = city ? await insightsApi.getCityStats(country, provinceCode, city) : undefined;
      if (!stats) stats = await insightsApi.getCityStats(country, provinceCode, undefined);
      return stats ?? null;
    })().catch(() => null),
    (city
      ? insightsApi.getTopCities(country, provinceCode, undefined, 5, false, undefined, city)
      : insightsApi.getTopCities(country, provinceCode, undefined, 5)
    ).catch(() => undefined),
    getCityInsights(slug).catch(() => null),
  ]);

  const popularAreaCards = topCities?.length
    ? topCitiesToAreaCards(topCities, city || province.toUpperCase())
    : [];

  // Resolve the stored payload to the request locale, and decide whether the
  // client should trigger (on-demand) generation: either nothing is stored yet,
  // or the row is missing the current locale's translation. When a non-English
  // locale's translation is missing, the AI prose is stripped so only the Deep
  // Dive's Overview card is hidden (numeric cards still render) until generation
  // backfills the translation.
  const cityInsights = storedCityInsights ? selectLocalizedCityInsights(storedCityInsights, locale) : null;
  const requestedLang = normalizeLocaleToInsightsLang(locale);
  const insightsGenerateLocale = !storedCityInsights
    ? requestedLang
    : isLocaleTranslationMissing(storedCityInsights, locale)
      ? requestedLang
      : null;

  return {
    location: {
      slug,
      name,
      latitude: userLocation?.latitude ?? 0,
      longitude: userLocation?.longitude ?? 0,
      country,
      province,
      city,
      hasAreaPage: true,
    },
    cityStats,
    popularAreaCards,
    cityInsights,
    insightsGenerateLocale,
  };
}

export type RootPageProps = Omit<HomePageProps, 'availableHomeListings' | 'availableHomeListingsV2'> & {
  availableHomeListingsData: AreaPageListing[];
  availableHomeListingsDataV2?: ExpListingCardDataWithCompliance[];
  soldHomeListingsData?: AreaPageListing[];
  featuredHomeListingsData?: AreaPageListing[];
  homePageUrls?: GenerateUrlsResult;
  enableLocalFocusHomepage?: boolean;
  enableZoocasaLightHomepage?: boolean;
  homepageInsights?: HomePageInsights | null;
} & AgntHomeRootProps;

// Handler for homepage server-side props
async function getHomePageServerSideProps(
  context: GetServerSidePropsContext,
  features: FeaturesType,
  tenant: TenantTypes
): Promise<GetServerSidePropsResult<RootPageProps>> {
  context.res.setHeader(CACHE_CONTROL_HEADER_NAME, 'public, s-maxage=43200, stale-while-revalidate=60');

  const { req: request } = context;
  const isCrawler = isUserAgentCrawler(request.headers);

  // AGNT shares this route but has no listings home — delegate to its own
  // module for the investor-relations landing's props.
  if (isTenantAgntInc(tenant.id)) {
    return getAgntHomeServerSideProps(context, tenant);
  }
  const { featuredHomesMlsNums } = await getHomeConfig();
  const notificationBannerCookie = getObjectFromRequestCookie<boolean>(HIDE_NOTIFICATION_BANNER_COOKIE_NAME, request);
  const isNotificationBannerHidden = !configJSON.features.useNotificationBanner || !!notificationBannerCookie;
  const headerFeatures = getFeaturesOverrideFromRequest(request);
  const enableLocalFocusHomepage =
    getStringFromRequestCookie(ENABLE_LOCAL_FOCUS_HOMEPAGE_COOKIE_NAME, request) === 'true' ||
    headerFeatures?.['useLocalFocusHomepage'] === true;
  // Get homepage listings server-side
  const userLocation = await getUserLocationFromServerSideRequest(request);
  const siteLocation = getSiteLocationFromRequest(request, userLocation?.countryCode);
  let parsedUserLocation = userLocation;
  // If user is not in country of SiteLocation, set defaults
  parsedUserLocation = getSupportedUserLocation(tenant.id, userLocation);

  let headerData: HeadDataType | null = null;
  const isUSTenant = isTenantExpUS(tenant.id) || isTenantExpInService(tenant.id) || isTenantExpCommercial(tenant.id);
  const isCATenant = isTenantExpCA(tenant.id);
  const isInServiceTenant = isTenantExpInService(tenant.id); // TODO: temp fix last search

  if (isZoocasaTenant(tenant.id)) {
    headerData = siteLocation === CountryCodeList.UNITED_STATES ? ZOO_USA_HEAD_DATA : ZOO_CA_HEAD_DATA;
  }

  const lastSearchLocation = getObjectFromRequestCookie<LastSearchLocation>(getLastSearchLocationTag(tenant.theme.id), request);

  const fallbackLocation = siteLocation === CountryCodeList.UNITED_STATES ? defaultUSCityPayload : defaultCACityPayload;
  let homePageUrls: GenerateUrlsResult = {
    homesForSaleUrl: `/${fallbackLocation.slug}-real-estate`,
    soldPricesUrl: `/${fallbackLocation.slug}-real-estate/sold`,
    mapViewUrl: '/search',
  };

  homePageUrls = await generateHomepageUrls(
    lastSearchLocation,
    userLocation,
    siteLocation,
    tenant.id
  );

  const locationSource = (lastSearchLocation?.description && getLastSearchDescription(lastSearchLocation)) || parsedUserLocation?.name || defaultCACityPayload.name;
  const locationGroup = lastSearchLocation?.group;
  const locationName = extractLocationForTenant(
    locationSource,
    tenant.id,
    userLocation,
    locationGroup
  );

  const enableZoocasaLightHomepage = (tenant.features.UseZoocasaLightHomepage ?? false) || (headerFeatures?.['useZoocasaLightHomepage'] === true);

  const usesLocalFocusHomepage = isCATenant || (isTenantExpUS(tenant.id) && (Boolean(tenant.features.UseLocalFocusHomepage) || enableLocalFocusHomepage));
  let homepageInsights: HomePageInsights | null = null;
  if (usesLocalFocusHomepage) {
    const insightsCountry = isCATenant ? CountryCodeList.CANADA : CountryCodeList.UNITED_STATES;
    // Seed insights from the user's last search first (what the client resolves
    // to on mount) so the correct city renders on first paint instead of the
    // geo/IP default, which the client would otherwise replace after an async
    // fetch. Falls back to the geo/IP user location when no city-level last
    // search is available.
    const insightsSeedLocation = resolveLastSearchInsightsLocation(lastSearchLocation) ?? parsedUserLocation;
    try {
      homepageInsights = await resolveHomepageInsights(insightsCountry, insightsSeedLocation, context.locale);
    } catch (error: any) {
      console.error(`Failed to resolve homepage insights server-side: ${error?.message || error}`);
    }
  }

  let availableHomeListingsData: AreaPageListing[];
  let availableCount: number;
  let isExpandedArea: boolean;

  const { routeMatchObject } = extractUrlData(context);
  const provinceOrState: string = sanitizeProvinceOrStateCode(routeMatchObject.provinceCode);
  const country = routeMatchObject.country?.toUpperCase() as CountryCode || countryCodeFromProvinceOrState(provinceOrState);
  const insightsApi = createInsightsApi();

  const generateInternalLinks = async () => {
    switch (tenant.id) {
      case EXP_REALTY_CA_TENANT_ID:
      case EXP_REALTY_US_TENANT_ID:
      case EXP_IN_SERVICE_TENANT_ID:
        return getTenantHomeConfig(tenant).internalLinks;
      case ZOOCASA_TENANT_ID: {
        const { createHomeConfig } = await import('@tenants/definitions/zoocasa/home-config');
        if (siteLocation === CountryCodeList.UNITED_STATES) { // Use the dynamic createHomeConfig function based on siteLocation
          const homeConfig = createHomeConfig(siteLocation);
          try {
            const dynamicLinks = await generateInternalLinksForCountry(siteLocation, insightsApi, homeConfig.internalLinks);
            if (dynamicLinks && dynamicLinks.length > 0) {
              return dynamicLinks;
            }
          } catch (error) {
            console.error('Error generating dynamic internal links:', error);
          }
          // Fallback to static data excluding Popular USA Searches
          return homeConfig.internalLinks?.filter(section => section.title !== 'Popular USA Searches') || [];
        }
        return createHomeConfig(siteLocation).internalLinks || [];
      }
      default: {
        const { createHomeConfig } = await import('@tenants/definitions/zoocasa/home-config');
        return createHomeConfig(siteLocation).internalLinks || [];
      }
    }
  };
  const internalLinksData = await generateInternalLinks() as LinkDataType[];

  const useLastSearchLocation = isLastSearchLocationValid(lastSearchLocation) && (!checkLocationHasAreaPageWithId(lastSearchLocation.id) || lastSearchLocation.group === MILITARY_BASES_SEARCH_PREDICTION_GROUP);
  if (useLastSearchLocation) {
    const { north, south, east, west } = getNSEWWithBoundaryString(lastSearchLocation.boundary);
    const availableRes = await fetchHomePageListingsWithBoundary({ north, south, east, west }, tenant.theme.id, false, features.useLegacySearchFilter || false);
    const { totalCount, listings } = availableRes;
    if (totalCount > 0) {
      availableCount = totalCount;
      availableHomeListingsData = listings.map<AreaPageListing>((listing: AreaPageListing) => AreaPageListing.toJSON(listing) as AreaPageListing);
      isExpandedArea = true;
      return {
        props: {
          isCrawler,
          featuredHomesMlsNums,
          isNotificationBannerHidden,
          availableHomeListingsData,
          availableHomeListingsMeta: availableCount,
          availableHomeListingsLocation: locationName,
          isExpandedArea,
          internalLinksData,
          tenantId: tenant.id,
          headerData,
          enableLocalFocusHomepage,
          enableZoocasaLightHomepage,
          homepageInsights,
          homePageUrls: {
            ...homePageUrls,
            soldPricesUrl: homePageUrls.soldPricesUrl || '',
          },
        }};
    }
  }
  // Fallback to slug based search if no listings obtained from boundary search
  const { params, slugFallback } = extractUrlData(context);
  const { filter } = params;
  const parsedLastSearchSlug = lastSearchLocation?.slug
    ? lastSearchLocation.slug
    : lastSearchLocation?.id?.replace(/^location-/, '');

  let slug: string;
  let finalCountry: CountryCode;

  const getValidSlugForCountry = (country: CountryCode): string => {
    // If the last search result was a military base, the slug will not have a state code, so we need to return the slug directly
    if (parsedLastSearchSlug && isInServiceTenant && lastSearchLocation?.group === MILITARY_BASES_SEARCH_PREDICTION_GROUP) {
      return parsedLastSearchSlug;
    }

    // Try last search location first, and compare with site location
    if (parsedLastSearchSlug && countryCodeFromProvinceOrState(parsedLastSearchSlug.slice(-2)) === country) {
      return parsedLastSearchSlug;
    }

    // Try to get prov or state from user location next, and compare with site location
    if (parsedUserLocation?.slug && countryCodeFromProvinceOrState(parsedUserLocation.slug.slice(-2)) === country) {
      return parsedUserLocation.slug;
    }

    // If siteLocation does not match with user or last search location, return fallback
    return country === CountryCodeList.CANADA ? defaultCACityPayload.slug : defaultUSCityPayload.slug;
  };

  if (isCATenant) {
    finalCountry = CountryCodeList.CANADA;
    slug = getValidSlugForCountry(CountryCodeList.CANADA);
  } else if (isUSTenant) {
    finalCountry = CountryCodeList.UNITED_STATES;
    slug = getValidSlugForCountry(CountryCodeList.UNITED_STATES);
  } else {
    const parsedSlugWithFallback = parsedLastSearchSlug || parsedUserLocation?.slug || defaultCACityPayload.slug;
    const areaPageSlug = grabSlug(parsedSlugWithFallback, slugFallback) || '';

    finalCountry = parsedSlugWithFallback.length === 0
      ? CountryCodeList.CANADA
      : (!isCrawler ? countryCodeFromProvinceOrState(parsedSlugWithFallback?.slice(-2)) : country);
    slug = parsedSlugWithFallback && !isCrawler ? parsedSlugWithFallback : areaPageSlug;
  }

  // Option 1a: when there's no last-search cookie and the user's geolocated slug isn't in
  // Go-Search's address registry, the slug-based fetch silently falls back to country-wide
  // listings. Supplying a boundary around the user's coordinates lets the fetcher replace that
  // meaningless 100k+ country teaser with realistic nearby listings. When this path fires,
  // `usedFallbackBoundary` comes back true and we re-route the "homes for sale" link at the
  // map-search page below.
  const userHasCoords = typeof userLocation?.latitude === 'number'
    && typeof userLocation?.longitude === 'number'
    && !(userLocation.latitude === 0 && userLocation.longitude === 0);
  const fallbackBoundary = userHasCoords
    ? boundaryAroundPoint(userLocation.latitude, userLocation.longitude)
    : undefined;

  const availableListingsQuery: ListingsQueryType = {
    country: finalCountry,
    slug: slug,
    filter: {
      ...filter,
      homeType: defaultHomeType,
      status: AVAILABLE_STATUS,
      hasImage: true,
      tenant: tenant.theme.id,
    },
    pageSize: 15,
    useLegacySearchFilter: false,
    fallbackBoundary,
  };
  const availableRes = await fetchHomePageListings(availableListingsQuery);
  const { totalCount, listings, isExpandedArea: isExpanded, usedFallbackBoundary } = availableRes;

  availableCount = totalCount;
  availableHomeListingsData = listings.map<AreaPageListing>((listing: AreaPageListing) => AreaPageListing.toJSON(listing) as AreaPageListing);
  isExpandedArea = isExpanded;

  if (usedFallbackBoundary && userLocation) {
    // The teaser is showing listings near the user's coordinates instead of an area page, so the
    // link must go to the map search page (same URL shape as mapViewUrl). Leave the title alone:
    // `locationName` from `extractLocationForTenant` already renders the user's city (e.g.
    // "Willowdale, ON"), which is the truth we want to keep.
    homePageUrls = {
      ...homePageUrls,
      homesForSaleUrl: buildMapSearchUrlForUserLocation(userLocation),
    };
  }

  // Transform with compliance if tenant has the feature enabled
  const useListingCardWithCompliance = tenant.features.UseListingCardV2 ?? false;
  const availableHomeListingsDataV2 = useListingCardWithCompliance
    ? toExpListingCardDataWithCompliance(
      listings,
      providerId => getComplianceRequirementFromRequest(request, providerId)
    )
    : null;

  // Fetch sold listings server-side
  let soldHomeListingsData: AreaPageListing[] = [];


  try {

    if (userLocation?.slug || lastSearchLocation) {
      if (isLastSearchLocationValid(lastSearchLocation) && !checkLocationHasAreaPageWithId(lastSearchLocation.id)) {
        // If last search doesn't have an area page, use boundary search
        const { north, south, east, west } = getNSEWWithBoundaryString(lastSearchLocation.boundary);
        const listingsRes = await fetchHomePageListingsWithBoundary({ north, south, east, west }, tenant.theme.id, true, features.useLegacySearchFilter || false);
        const { listings: soldListings } = listingsRes;
        soldHomeListingsData = soldListings.map<AreaPageListing>((listing: AreaPageListing) => AreaPageListing.toJSON(listing) as AreaPageListing);
      } else {
        // Fallback to slug based search
        const slugForSold: string = (getLocationSlugFromId(lastSearchLocation?.id || '') || parsedUserLocation?.slug) ?? '';
        const countryForSold: CountryCode = countryCodeFromProvinceOrState(slugForSold?.slice(-2)) || CountryCodeList.CANADA;

        const soldListingsQuery: ListingsQueryType = {
          country: countryForSold,
          slug: slugForSold,
          filter: {
            status: NOT_AVAILABLE_SOLD_STATUS,
            homeType: defaultHomeType,
            hasImage: tenant.theme.id !== ThemeNames.ZOOCASA,
            tenant: tenant.theme.id,
          },
          pageSize: 10,
          useLegacySearchFilter: false,
        };
        const soldRes = await fetchHomePageListings(soldListingsQuery);
        soldHomeListingsData = soldRes.listings.map<AreaPageListing>((listing: AreaPageListing) => AreaPageListing.toJSON(listing) as AreaPageListing);
      }
    }
  } catch (error: any) {
    console.error(`Failed to fetch sold listings server-side: ${error?.message || error}`);
  }

  // Fetch featured listings server-side
  let featuredHomeListingsData: AreaPageListing[] = [];
  try {
    if (featuredHomesMlsNums?.length) {
      const featuredListings: AreaPageListing[] = await getListingByMls(featuredHomesMlsNums, featuredHomesMlsNums.length);
      // Create a map of MLS numbers to their index in the original array
      const mlsIndexMap = new Map(featuredHomesMlsNums.map((mls, index) => [mls, index]));

      const filteredAndSorted = featuredListings
        .filter(listing => listing.statusId === AreaPageListing_Status.Available)
        // Sort by original order first
        .sort((a, b) => {
          const indexA = mlsIndexMap.get(a.mls) ?? Infinity;
          const indexB = mlsIndexMap.get(b.mls) ?? Infinity;
          return indexA - indexB;
        });

      featuredHomeListingsData = filteredAndSorted.map<AreaPageListing>((listing: AreaPageListing) => AreaPageListing.toJSON(listing) as AreaPageListing);
    }
  } catch (error: any) {
    console.error(`Failed to fetch featured listings server-side: ${error?.message || error}`);
  }

  // Fetch insights cards for commercial tenants
  let insightsCards: Array<{ title: string; subtitle?: string; date: string; url: string; image: string }> = [];
  if (isTenantExpCommercial(tenant.id, features) || isTenantExpCommercialCa(tenant.id, features)) {
    try {
      const response = await getBlogArticlesByOffset({
        limit: 4,
        offset: 0,
        excludeCategories: [COMMERCIAL_LIFE_CATEGORY_ID],
        wpApi: CommercialBlogPostsApi,
      });

      insightsCards = response.articles.map(article => ({
        title: article.title,
        subtitle: 'Real Estate Insights',
        date: formatI18nDate(article.date, 'short'),
        url: `${INSIGHTS_BLOG_PATH}${new URL(article.link).pathname}`,
        image: article.thumbnail,
      }));
    } catch (error: any) {
      console.error(`Failed to fetch insights cards server-side: ${error?.message || error}`);
    }
  }

  return {
    props: {
      isCrawler,
      featuredHomesMlsNums,
      isNotificationBannerHidden,
      availableHomeListingsData,
      availableHomeListingsDataV2,
      availableHomeListingsMeta: availableCount,
      availableHomeListingsLocation: locationName,
      isExpandedArea,
      internalLinksData,
      tenantId: tenant.id,
      headerData,
      soldHomeListingsData,
      featuredHomeListingsData,
      enableLocalFocusHomepage,
      enableZoocasaLightHomepage,
      homepageInsights,
      homePageUrls: {
        ...homePageUrls,
        soldPricesUrl: homePageUrls.soldPricesUrl || '',
      },
      insightsCards,
    }};
}

export default function RootPage({
  isCrawler,
  featuredHomesMlsNums,
  isNotificationBannerHidden,
  availableHomeListingsData,
  availableHomeListingsDataV2,
  availableHomeListingsMeta,
  availableHomeListingsLocation,
  isExpandedArea = false,
  internalLinksData,
  tenantId: tenantName,
  headerData,
  soldHomeListingsData = [],
  featuredHomeListingsData = [],
  homePageUrls,
  insightsCards,
  enableLocalFocusHomepage,
  enableZoocasaLightHomepage,
  homepageInsights,
  agntSeo = null,
  agntNewsCards,
  agntStock = null,
}: RootPageProps) {

  const availableHomeListings = useMemo(() =>
    availableHomeListingsData
      .map((listing: AreaPageListing) => AreaPageListing.fromJSON(listing))
      .map((listing: AreaPageListing) => areaPageListingToListingCardData(listing)),
  [availableHomeListingsData]);

  const soldHomeListings = useMemo(() =>
    soldHomeListingsData
      .map((listing: AreaPageListing) => AreaPageListing.fromJSON(listing))
      .map((listing: AreaPageListing) => areaPageListingToListingCardData(listing)),
  [soldHomeListingsData]);

  const featuredHomeListings = useMemo(() =>
    featuredHomeListingsData
      .map((listing: AreaPageListing) => AreaPageListing.fromJSON(listing))
      .map((listing: AreaPageListing) => areaPageListingToListingCardData(listing)),
  [featuredHomeListingsData]);

  if (isTenantAgntInc(tenantName)) {
    return <AgntHomeRoot agntSeo={agntSeo} agntNewsCards={agntNewsCards} agntStock={agntStock} />;
  }

  return <HomePage
    isCrawler={isCrawler}
    featuredHomesMlsNums={featuredHomesMlsNums}
    isNotificationBannerHidden={isNotificationBannerHidden}
    availableHomeListings={availableHomeListings}
    availableHomeListingsV2={availableHomeListingsDataV2}
    availableHomeListingsMeta={availableHomeListingsMeta}
    availableHomeListingsLocation={availableHomeListingsLocation}
    isExpandedArea={isExpandedArea}
    internalLinksData={internalLinksData}
    routeName={ROOT_ROUTE}
    tenantId={tenantName}
    headerData={headerData}
    soldHomeListings={soldHomeListings}
    featuredHomeListings={featuredHomeListings}
    homePageUrls={homePageUrls}
    insightsCards={insightsCards}
    enableLocalFocusHomepage={enableLocalFocusHomepage}
    enableZoocasaLightHomepage={enableZoocasaLightHomepage}
    homepageInsights={homepageInsights}
  />;

}

async function getHomeConfig() {
  const namedContentsApi = createNamedContentsApi();
  const config = await namedContentsApi.getHomeConfigNamedContent();
  return (config && config?.content) || { featuredHomesMlsNums: []};
}

//FIXME: remove this
export function extractUrlData(context: GetServerSidePropsContext, showMapCardExperimentView?: boolean) {
  const { query, resolvedUrl } = context;
  const routeMatchObject: AreaListingsRouteMatchObject = generateRouteMatchObjectFromPath(resolvedUrl, false);
  const filterFromRouteMatchObject: PartialDeep<Filter> = routeMatchObject.filter;
  const url = new URL(`https://example.com${resolvedUrl}`);
  const urlSearchParams: URLSearchParams = new URLSearchParams(url.search);
  const params: Required<ListingParams> = {
    sort: getSortFromURLSearchParams(urlSearchParams),
    page: {
      number: Math.max(extractPageFromUrl(query), DEFAULT_PAGE_NUMBER),
      size: showMapCardExperimentView ? AREA_PAGE_CONSTANTS.DEFAULT_PAGE_SIZE - 1 : AREA_PAGE_CONSTANTS.DEFAULT_PAGE_SIZE,
    },
    filter: { ... filterFromRouteMatchObject },
  } as Required<ListingParams>;
  const slugFallback = getSlugFallbackFromURLSearchParams(urlSearchParams);
  return { params, path: resolvedUrl, routeMatchObject, slugFallback };
}

//FIXME: remove this
function grabSlug(slug: string, slugFallback: string | null) {
  let areaPageSlug = (slugFallback && slugFallback?.length > 0) ? slugFallback : slug;
  if (areaPageSlug.includes('/')) {
    areaPageSlug = areaPageSlug.replace(/^(.*?)\/(.*)$/, '$2-$1');
  }
  return areaPageSlug;
}

export const areaPageListingToListingCardData: (listing: AreaPageListing) => ListingCardData = (listing: AreaPageListing) => (new AreaPageListingCardData(listing));

// Export the wrapped handler with shared server-side props
export const getServerSideProps = createGetServerSideProps<RootPageProps>(getHomePageServerSideProps, [Namespaces.HOME, Namespaces.INTERNAL_LINKS]);
